export const ROLES = Object.freeze({
  ADMINISTRADOR: 'ADMINISTRADOR',
  ADMINISTRADOR_GENERAL: 'ADMINISTRADOR_GENERAL',
  COORDINADOR: 'COORDINADOR',
  PUNTERO: 'PUNTERO',
  VOTANTE: 'VOTANTE',
});

export const ROLE_LABELS = Object.freeze({
  [ROLES.ADMINISTRADOR]: 'Administrador',
  [ROLES.ADMINISTRADOR_GENERAL]: 'Administrador general',
  [ROLES.COORDINADOR]: 'Coordinador',
  [ROLES.PUNTERO]: 'Puntero',
  [ROLES.VOTANTE]: 'Votante',
});

export const ACTIONS = Object.freeze({
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  CALL: 'call',
  VALIDATE: 'validate',
  RESOLVE: 'resolve',
  ASSIGN: 'assign',
  EXPORT: 'export',
  CONFIGURE: 'configure',
});

export const RESOURCES = Object.freeze({
  DASHBOARD: 'dashboard',
  STRUCTURE: 'structure',
  VOTERS: 'voters',
  CALL_SHEETS: 'callSheets',
  DUPLICATES: 'duplicates',
  VEHICLES: 'vehicles',
  DRIVERS: 'drivers',
  FOLLOW_UPS: 'followUps',
  REPORTS: 'reports',
  USERS: 'users',
  AUDIT: 'audit',
  SETTINGS: 'settings',
  FINANCE: 'finance',
  ELECTION_DAY: 'electionDay',
});

export const createPermission = (resource, action) => `${resource}.${action}`;

const permissions = (resource, actions) =>
  actions.map((action) => createPermission(resource, action));

const allActions = Object.values(ACTIONS);
const administratorResources = Object.values(RESOURCES).filter(
  (resource) => ![RESOURCES.CALL_SHEETS, RESOURCES.FOLLOW_UPS].includes(resource)
);
const allPermissions = administratorResources.flatMap((resource) =>
  permissions(resource, allActions)
);

export const ROLE_PERMISSIONS = Object.freeze({
  [ROLES.ADMINISTRADOR]: allPermissions,
  [ROLES.ADMINISTRADOR_GENERAL]: [
    ...permissions(RESOURCES.DASHBOARD, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.STRUCTURE, [ACTIONS.VIEW, ACTIONS.CREATE]),
    ...permissions(RESOURCES.VOTERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.CALL_SHEETS, [ACTIONS.VIEW, ACTIONS.CALL, ACTIONS.VALIDATE]),
    ...permissions(RESOURCES.DUPLICATES, [ACTIONS.VIEW, ACTIONS.RESOLVE]),
    ...permissions(RESOURCES.VEHICLES, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.DRIVERS, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.FOLLOW_UPS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.REPORTS, [ACTIONS.VIEW, ACTIONS.EXPORT]),
    ...permissions(RESOURCES.USERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.FINANCE, [ACTIONS.VIEW, ACTIONS.EDIT]),
    ...permissions(RESOURCES.ELECTION_DAY, [ACTIONS.VIEW]),
  ],
  [ROLES.COORDINADOR]: [
    ...permissions(RESOURCES.DASHBOARD, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.STRUCTURE, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.VOTERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.ASSIGN]),
    ...permissions(RESOURCES.VEHICLES, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.ASSIGN]),
    ...permissions(RESOURCES.DRIVERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.ASSIGN]),
    ...permissions(RESOURCES.REPORTS, [ACTIONS.VIEW, ACTIONS.EXPORT]),
    ...permissions(RESOURCES.USERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.ELECTION_DAY, [ACTIONS.VIEW, ACTIONS.EDIT]),
  ],
  [ROLES.PUNTERO]: [
    ...permissions(RESOURCES.DASHBOARD, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.STRUCTURE, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.VOTERS, [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT]),
    ...permissions(RESOURCES.VEHICLES, [ACTIONS.VIEW]),
    ...permissions(RESOURCES.ELECTION_DAY, [ACTIONS.VIEW, ACTIONS.EDIT]),
  ],
  [ROLES.VOTANTE]: [],
});

export const EDITABLE_FIELDS_BY_ROLE = Object.freeze({
  [ROLES.ADMINISTRADOR_GENERAL]: Object.freeze({
    [RESOURCES.VOTERS]: ['telefono', 'direccion', 'barrio', 'referencia', 'ciudad', 'local_votacion', 'mesa', 'necesita_movil', 'observaciones', 'estado', 'classification'],
    [RESOURCES.CALL_SHEETS]: ['status', 'validationNotes'],
    [RESOURCES.USERS]: ['name', 'email', 'zone', 'status'],
  }),
  [ROLES.COORDINADOR]: Object.freeze({
    [RESOURCES.USERS]: ['name', 'email', 'zone', 'status'],
  }),
});

export const hasRole = (user, allowedRoles = []) =>
  allowedRoles.length === 0 || allowedRoles.includes(user?.role);

export const hasPermission = (user, permission) =>
  !permission || Boolean(user?.permissions?.includes(permission));

export const canPerform = (user, resource, action) =>
  hasPermission(user, createPermission(resource, action));

export const canAccess = (user, allowedRoles, permission) =>
  Boolean(user) &&
  hasRole(user, allowedRoles) &&
  hasPermission(user, permission);

export const canEditField = (user, resource, field) => {
  if (!canPerform(user, resource, ACTIONS.EDIT)) return false;
  if (user.role === ROLES.ADMINISTRADOR) return true;
  const restrictedFields = EDITABLE_FIELDS_BY_ROLE[user.role]?.[resource];
  return restrictedFields ? restrictedFields.includes(field) : true;
};
